/**
 * The free sample picker: one sample per bag, and the whole exchange lives in
 * the accordion. Its header names the pick (the picked line never renders in
 * the list; ia-cart-items skips it), a tap folds the panel and rewrites the
 * header on the spot, and only a failed write reopens it.
 *
 * The first sample is pre-added (`_sample_auto`) so checkout is always right,
 * and stays invisible until a pick turns it into an answer: the shopper must
 * feel they chose. `_sample_source` deliberately differs from the gifts'
 * `_gwp_source`, or the tier engine's orphan sweep would take our line.
 *
 * State that must survive the data-re-render swap lives OUTSIDE the element,
 * in a per-drawer map: the element is replaced on every render, so it cannot
 * carry the flags, and module-wide singletons made two drawers (an editor
 * preview beside the page) share one write lock and paint each other's lines.
 */
import { GRANT_MARK, hasPurchasedLine, holdCart, isSampleLine, refusalContext } from "ia-cart-api";
import { captureError } from "ia-error-report";
import { IaCartBlock } from "ia-cart-block";

const PROP = "_sample_source";
const AUTO = "_sample_auto";

/** An add can resolve without landing: a dead variant 422s and the API swallows it. */
function confirmLanded(cart, id, where, extra = {}) {
  // A refused or failed add answers with no cart and was already reported
  // where it was written (cart:add); a second event here tells it twice.
  if (!cart) return false;
  const landedLine = (cart?.items || []).find(
    (item) => item.properties?.[PROP] && String(item.variant_id) === String(id)
  );
  if (!landedLine) {
    const error = new Error(`sample ${id} was due but did not land`);
    console.error("[ia-cart-sample]", error.message);
    // The picker lines the answer came back with, the theme's own starred:
    // a refusal with an unstarred one names the line the bag held unseen.
    const pickerLines = (cart?.items || [])
      .filter((item) => item.product_type === "sample-picker")
      .map((item) => `${item.variant_id}${item.properties?.[PROP] ? "*" : ""}`)
      .join(",");
    captureError(error, { where, variant: String(id), pickerLines, ...extra });
    return false;
  }
  // The drawer reads FREE; only a store-side discount makes checkout agree.
  if (landedLine.final_price > 0) {
    const priced = new Error(`sample ${id} is priced in the cart, checkout will charge it`);
    console.error("[ia-cart-sample]", priced.message);
    captureError(priced, { where: "sample:priced", variant: String(id), price: landedLine.final_price });
  }
  return true;
}

// Auto-grants per page, per block: one is normal, a third means something
// keeps taking the default back out and the shopper watches it blink.
const autoGrants = new Map();

/**
 * Per-drawer flags: writing (the swap lock), staleRemovals (consecutive
 * removals lost to a reshuffled line key: one miss is the retry the refresh
 * provides, two stops the writes), queuedPick (replayed when the lock lifts:
 * a swallowed tap reads as a broken button), pending (the newest answer no
 * write has settled yet), rememberedPick (the answer whose line the empty-bag
 * sweep removed, waiting to come back), missed (a cart change arrived under
 * the lock; the reconciler reruns when it lifts).
 */
const states = new WeakMap();

function stateOf(el) {
  const root = el.closest?.("ia-cart") || document;
  let state = states.get(root);
  if (!state) {
    state = {
      writing: false,
      staleRemovals: 0,
      queuedPick: null,
      pending: null,
      rememberedPick: null,
      missed: false,
      autoRefused: false,
      granting: false,
    };
    states.set(root, state);
  }
  return state;
}

/**
 * The element may have been replaced while the write travelled, and a detached
 * one no longer finds its drawer (its state would read as a fresh, empty one):
 * the drawer is captured while attached, and the replay goes to whichever
 * instance of it is live now.
 */
function replayPick(root, el) {
  const live = root.querySelector?.("ia-cart-samples") || el;
  const state = live.state;
  if (!state.queuedPick) return;
  const id = state.queuedPick;
  state.queuedPick = null;
  live.pick({ dataset: { sampleAdd: id } });
}

class IaCartSamples extends IaCartBlock {
  get state() {
    return stateOf(this);
  }

  /** The drawer this instance belongs to; controls are looked up in it only. */
  get root() {
    return this.closest?.("ia-cart") || document;
  }

  bind() {
    this.addEventListener("click", (event) => {
      const button = event.target.closest("[data-sample-add]");
      if (button && !button.disabled) this.pick(button);
    });
  }

  // Against the seeded cart, so the sample arrives on load with no shopper touch.
  apiReady() {
    this.sync();
    this.reconcile();
  }

  cartChanged() {
    this.sync();
    this.reconcile();
  }

  /** Ordered as the merchant listed them: the first is the pre-added default. */
  get variants() {
    return (this.dataset.variants || "").split(",").filter(Boolean);
  }

  get autoAdd() {
    return this.dataset.autoAdd === "true";
  }

  sampleLines(lines) {
    return (lines || []).filter((item) => isSampleLine(item));
  }

  /** Rows report the shopper's ANSWER, not the bag: a pre-added line is not one. */
  sync(chosenId) {
    const lines = this.api?.cart ? this.api.predictedLines() : [];
    const picked = this.sampleLines(lines).find((item) => !item.properties?.[AUTO]);
    // An unsettled pick outranks the bag: mid-swap the bag is between samples
    // and would name nothing.
    const current = chosenId || this.state.pending || String(picked?.variant_id || "");
    this.querySelectorAll("[data-sample-add]").forEach((button) => {
      const isCurrent = current && button.dataset.sampleAdd === String(current);
      button.setAttribute("aria-pressed", String(Boolean(isCurrent)));
      button.disabled = Boolean(isCurrent);
    });
    this.applyTitle(current);
    this.syncLines(current, lines);
  }

  /**
   * The pick is shown once, in the header. A line this picker cannot name
   * (its product left the block's list) comes back as a line instead.
   *
   * A swap holds both samples for the frames between the add and the removal,
   * and the outgoing one is nameless in exactly that window, so an unsettled
   * drawer keeps every sample line put away and answers once the write lands.
   * @param {string} current - the answered variant id, empty when none
   * @param {Array} lines - predicted cart lines
   */
  syncLines(current, lines) {
    const named = Boolean(current) && Boolean(this.querySelector(`[data-sample-add="${current}"]`));
    const variantByKey = new Map((lines || []).map((item) => [String(item.key), String(item.variant_id)]));
    const settling = Boolean(this.state?.writing) || Boolean(this.state?.pending);
    this.root?.querySelectorAll("ia-cart-item[data-sample-line]").forEach((line) => {
      const isNamed = named && variantByKey.get(line.dataset.key) === String(current);
      line.toggleAttribute("data-sample-in-panel", settling || isNamed);
    });
  }

  /**
   * Two faces the CSS switches on the fold: open always invites with the plain
   * title, folded it answers with the pick. Unpicked, both faces invite.
   */
  applyTitle(id) {
    const title = this.closest("ia-accordion-component")?.querySelector(".ia-accordion-title");
    if (!title) return;
    const name = id
      ? this.querySelector(`[data-sample-add="${id}"]`)
          ?.closest(".ia-cart-samples__row")
          ?.querySelector(".ia-cart-samples__title")
          ?.textContent?.trim()
      : "";
    const open = document.createElement("span");
    open.className = "ia-cart-samples__t-open";
    open.textContent = this.dataset.title || "";
    const closed = document.createElement("span");
    closed.className = "ia-cart-samples__t-closed";
    if (name) {
      const picked = document.createElement("span");
      picked.className = "ia-cart-samples__picked";
      picked.textContent = name;
      const label = document.createElement("span");
      label.className = "ia-cart-samples__label";
      label.textContent = this.dataset.selectedLabel || "Selected free sample:";
      closed.append(label, picked);
    } else {
      closed.textContent = this.dataset.title || "";
    }
    title.replaceChildren(open, closed);
  }

  /**
   * Swaps the sample: the old line goes out first, because the store caps
   * samples at one per order and refuses a second one outright. The hold keeps
   * a checkout tap waiting through the sampleless gap, and an add that fails
   * brings the old line back. Tapping the pre-added row IS a write; it turns
   * the default into an answer.
   */
  async pick(button) {
    const api = this.api;
    if (!api) return;
    const id = button.dataset.sampleAdd;
    const state = this.state;
    // A tap that lands while a write holds the lock is kept, not swallowed, and
    // it answers on screen right away: newest wins.
    if (state.writing) {
      state.queuedPick = id;
      state.pending = id;
      this.sync(id);
      return;
    }
    // Captured while attached: a re-render may replace this element mid-write.
    const root = this.root;
    const current = this.sampleLines(api.cart?.items)[0] || null;
    if (current && String(current.variant_id) === id && !current.properties?.[AUTO]) {
      // The row already picked: nothing to write, the panel just folds.
      this.closest("ia-accordion-component")?.close?.();
      return;
    }

    state.writing = true;
    state.pending = id;
    // A fresh answer outdates whatever the empty-bag sweep had remembered.
    state.rememberedPick = null;
    // Held for the pair, not per write: between them the bag has no sample and
    // a checkout tap would go without one.
    const release = holdCart();
    // Inside the try, all of it: a throw before it would leave the lock and the
    // hold standing, and the hold sits on checkout until the 4s cap.
    let removed = null;
    try {
      this.sync(id);
      this.closest("ia-accordion-component")?.close?.();
      if (current) {
        await api.updateQty({ key: current.key, qty: 0 });
        removed = current;
      }
      const cart = await api.addToCartJson({
        id,
        quantity: 1,
        properties: { [PROP]: this.dataset.source, [GRANT_MARK]: "1" },
      });
      // Gated on the add having LANDED: a failed add leaves the bag sampleless.
      if (!confirmLanded(cart, id, "sample:pick")) await undo(api, removed, root);
    } catch (error) {
      console.error("[ia-cart-sample] pick failed", error);
      captureError(error, { ...refusalContext(error, "sample:pick"), variant: String(id) });
      await undo(api, removed, root);
    } finally {
      release();
      state.writing = false;
      state.granting = false;
      state.pending = null;
      replayPick(root, this);
      replayMissedReconcile(root, this);
    }
  }

  /**
   * Desired state against actual, minimal writes (the gift reconciler's shape):
   * pre-add on the first purchase, take every sample out with the last one
   * (a sample cannot be the bag), drop leftovers, clamp a doubled line.
   */
  async reconcile() {
    const api = this.api;
    const state = this.state;
    // Captured while attached: the write's re-render replaces this element.
    const root = this.root;
    if (!api?.cart?.items) return;
    // Never on the page's own seed: a line the server holds and the seed lacks
    // would be granted twice, and the store allows one sample.
    await api.verified;
    // The lock drops this PASS, not the change: the flag reruns the reconciler
    // when the lock lifts, always against the freshest cart.
    if (state.writing) {
      state.missed = true;
      return;
    }
    // A grant that another pass made while this one waited is already the
    // answer: the bag holds a sample, and adding a second is what the store
    // refuses.
    if (state.granting) {
      state.missed = true;
      return;
    }
    // Two stale-key strikes on one page and this stops writing: every retry
    // rides a refresh->cartChanged->reconcile cycle, and a condition the
    // fresh keys did not cure would turn that cycle into a network loop.
    if (state.staleRemovals > 1) return;
    // Read after the wait, never the snapshot this pass entered with: the
    // verification can have replaced the cart while it was pending.
    const lines = api.cart.items;
    const purchased = hasPurchasedLine({ items: lines });

    // One write for every removal and clamp: a roundtrip repaints the drawer.
    const updates = {};
    let addFirst = false;

    // A line we never added is not ours to keep: no provenance mark, or a
    // product outside the merchant's configured list (which a copied mark
    // cannot fake). Real swaps are excluded by the `writing` lock above.
    const offered = this.variants;
    const [samples, foreign] = this.sampleLines(lines).reduce(
      ([ours, theirs], item) => {
        // Only a line CLAIMING our signature can be forged. A plain
        // sample-picker product the shopper added by hand is a legit sample.
        if (!item.properties?.[PROP]) {
          ours.push(item);
          return [ours, theirs];
        }
        const marked = Boolean(item.properties?.[GRANT_MARK]);
        const listed = !offered.length || offered.includes(String(item.variant_id));
        (marked && listed ? ours : theirs).push(item);
        return [ours, theirs];
      },
      [[], []]
    );
    foreign.forEach((item) => {
      updates[item.key] = 0;
    });
    if (foreign.length) {
      const forged = new Error(`${foreign.length} sample line(s) the picker never added, removing`);
      console.error("[ia-cart-sample]", forged.message);
      captureError(forged, {
        where: "sample:foreign",
        count: foreign.length,
        variants: foreign.map((item) => item.variant_id).join(","),
      });
    }

    let restoreId = null;
    if (!purchased) {
      // The ANSWER outlives its line: the shopper can re-earn the sample after
      // this sweep lands, and the next grant must restore their pick.
      const answered = samples.find((item) => !item.properties?.[AUTO]);
      if (answered) state.rememberedPick = String(answered.variant_id);
      samples.forEach((item) => {
        updates[item.key] = 0;
      });
    } else {
      // cart.items is newest first, so [0] is the shopper's latest answer
      const keep = samples[0] || null;
      samples.slice(1).forEach((item) => {
        updates[item.key] = 0;
      });
      if (samples.length > 1) {
        // More than one means a half-failed swap or two tabs racing.
        const dupes = new Error(`${samples.length} sample lines, keeping the newest`);
        console.error("[ia-cart-sample]", dupes.message);
        captureError(dupes, {
          where: "sample:dupes",
          count: samples.length,
          variants: samples.slice(0, 10).map((item) => item.variant_id).join(","),
        });
      }
      if (keep && keep.quantity > 1) {
        updates[keep.key] = 1;
        const grew = new Error(`the sample line grew to quantity ${keep.quantity}, clamping to one`);
        console.error("[ia-cart-sample]", grew.message);
        captureError(grew, { where: "sample:clamped", quantity: keep.quantity });
      }
      // A remembered answer comes back AS the answer, no `_sample_auto`; the
      // memory is one-shot, and a variant the block no longer lists falls
      // through to the auto-added default.
      if (!keep) {
        const remembered = state.rememberedPick;
        state.rememberedPick = null;
        if (remembered && this.variants.includes(remembered)) restoreId = remembered;
      }
      // A refused default stays refused for the page: the store said no to
      // this bag, and every retry would be the same add and the same no.
      addFirst = !keep && !restoreId && this.autoAdd && !state.autoRefused && Boolean(this.variants[0]);
    }

    if (Object.keys(updates).length === 0 && !addFirst && !restoreId) return;
    state.writing = true;
    // The restored answer rides `pending` over the write: the removal's repaint
    // sees no line yet and would paint the picker unanswered for a frame.
    let restoredPending = null;
    if (restoreId && !state.pending) {
      state.pending = restoreId;
      restoredPending = restoreId;
    }
    const release = holdCart();
    try {
      if (Object.keys(updates).length) await api.updateCart({ data: { updates } });
      if (restoreId) {
        const cart = await api.addToCartJson({
          id: restoreId,
          quantity: 1,
          properties: { [PROP]: this.dataset.source, [GRANT_MARK]: "1" },
        }, { silent: true });
        // A refused restore gets the default in this same pass: a failed add
        // publishes no cart change, so left to the next one the bag would
        // hold no sample at all. Not when the default IS the refused variant.
        if (!confirmLanded(cart, restoreId, "sample:restore")) {
          addFirst = this.autoAdd && Boolean(this.variants[0]) && this.variants[0] !== restoreId;
        }
      }
      if (addFirst) {
        state.granting = true;
        const grants = (autoGrants.get(this.dataset.source) || 0) + 1;
        autoGrants.set(this.dataset.source, grants);
        if (grants === 3) {
          const thrash = new Error(`the default sample was re-added ${grants} times on one page`);
          console.error("[ia-cart-sample]", thrash.message);
          captureError(thrash, { where: "sample:thrash", count: grants });
        }
        const first = this.variants[0];
        const cart = await api.addToCartJson({
          id: first,
          quantity: 1,
          properties: { [PROP]: this.dataset.source, [AUTO]: "1", [GRANT_MARK]: "1" },
        }, { silent: true });
        const landed = confirmLanded(cart, first, "sample:auto", {
          seen: this.sampleLines(lines).map((item) => item.variant_id).join(","),
          itemCount: api.cart?.item_count ?? -1,
        });
        if (!landed) state.autoRefused = true;
      }
      state.staleRemovals = 0;
    } catch (error) {
      // A line key is not persistent: Shopify reshuffles it when the line's
      // properties or discount applications change, and a removal aimed at
      // the old key answers 422 "updates parameter is invalid". updateCart
      // already refreshed, the refresh republishes cartChanged, and this
      // reconciler re-runs against fresh keys: the first miss IS that retry,
      // not a failure worth an event (the write itself is still reported once
      // as cart:update). Only a second consecutive miss speaks here.
      const stale = /updates parameter is invalid/i.test(String(error?.message || ""));
      if (stale && state.staleRemovals === 0) {
        state.staleRemovals = 1;
        console.warn("[ia-cart-sample] stale line key, the refresh retries with fresh ones");
      } else {
        if (stale) state.staleRemovals += 1;
        console.error("[ia-cart-sample] reconcile failed", error);
        captureError(error, refusalContext(error, "sample:reconcile"));
      }
    } finally {
      // Only the restore's own pending is dropped: a tap that landed mid-write
      // replaced it and is settled by its own write.
      if (restoredPending && state.pending === restoredPending) state.pending = null;
      release();
      state.writing = false;
      state.granting = false;
      replayPick(root, this);
      replayMissedReconcile(root, this);
    }
  }
}

/**
 * Reruns the reconciler for the change the lock turned away, on whichever
 * instance of this drawer's picker is live now; nothing missed is a no-op.
 */
function replayMissedReconcile(root, el) {
  const live = root.querySelector?.("ia-cart-samples") || el;
  const state = live.state;
  if (!state.missed) return;
  state.missed = false;
  live.reconcile();
}

/**
 * Takes a failed pick's paint back: the server never agreed to it. Runs on the
 * LIVE instance of this drawer's picker, because the optimistic render may
 * have swapped the element that started the write.
 */
/**
 * After a failed swap: the line taken out comes back as it was, so the bag
 * keeps a sample, and the paint comes off either way.
 */
async function undo(api, line, root) {
  if (line) {
    try {
      const cart = await api.addToCartJson({ id: line.variant_id, quantity: 1, properties: line.properties });
      confirmLanded(cart, line.variant_id, "sample:undo");
    } catch (error) {
      captureError(error, { ...refusalContext(error, "sample:undo"), variant: String(line.variant_id) });
    }
  }
  await unpaint(root);
}

async function unpaint(root) {
  const el = root.querySelector?.("ia-cart-samples");
  // Dropped before the refresh, not in the caller's finally: the render the
  // refresh triggers would otherwise repaint the answer the server refused.
  if (el) el.state.pending = null;
  await el?.api?.refresh?.().catch(() => {});
  const live = root.querySelector?.("ia-cart-samples");
  if (!live) return;
  live.sync();
  live.closest("ia-accordion-component")?.open?.();
}

if (!customElements.get("ia-cart-samples")) {
  customElements.define("ia-cart-samples", IaCartSamples);
}
